/**
 * @file ManagerDecideForm.tsx
 * @description 總經理批示子表單（PDF 第三節「總經理批示意見」）。
 *
 * 序60：總經理批示＝直接簽名——
 *   - 簽名優先「導入簽名檔」（員工檔案簽名，預設總經理鄭逢喜本人帳號簽名檔），可手寫替代；
 *   - 簽名設為**非必填**；
 *   - 簽名日期預設當日、可修改；
 *   - 批示意見改為選填。
 * 父層僅關心 onSubmit 回呼；簽名上傳交由父層注入 uploadSignature helper。
 */

import { useState } from 'react';
import { Button, DatePicker, Form, Input, Radio, Space, Tag } from 'antd';
import dayjs from 'dayjs';
import { message } from '@/lib/antd-app';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { SignaturePadModal } from '@/components/common/SignaturePadModal';
import type { ManagerDecideContractReviewInput } from '@zhcb/shared';

interface Props {
  /** 提交時的回呼。父層用 mutation 包裝實際 API 調用。 */
  onSubmit: (input: ManagerDecideContractReviewInput) => Promise<void>;
  /** 對應評審單版本（樂觀鎖） */
  version: number;
  /**
   * 上傳簽名後返回 fileId 的 helper。
   * 由父層注入，避免重複實作 OSS / files API。
   */
  uploadSignature: (file: File) => Promise<number>;
}

export function ManagerDecideForm({ onSubmit, version, uploadSignature }: Props) {
  const [form] = Form.useForm<{
    decision: 'accept' | 'reject_fixable' | 'reject_terminal';
    managerOpinion?: string;
    rejectionReason?: string;
  }>();
  const [submitting, setSubmitting] = useState(false);
  const [signatureFileId, setSignatureFileId] = useState<number | null>(null);
  // 序60：簽名日期（預設當日、可修改）
  const [signDate, setSignDate] = useState<dayjs.Dayjs>(dayjs());
  // 總經理手寫簽名彈窗開關 & 上傳中狀態
  const [sigOpen, setSigOpen] = useState(false);
  const [sigUploading, setSigUploading] = useState(false);

  const decision = Form.useWatch('decision', form);
  const needReason = decision && decision !== 'accept';

  // 序60.1：導入本人員工簽名檔（預設【鄭逢喜】＝總經理本人帳號的簽名檔）
  const importSignatureMut = useMutation({
    mutationFn: async () => apiClient.get<{ fileId: number | null }>('/api/auth/me/signature'),
    onSuccess: (res) => {
      if (res.fileId) {
        setSignatureFileId(res.fileId);
        message.success('已導入我的簽名檔');
      } else {
        message.warning('尚未設定簽名檔，請至員工檔案上傳簽名，或改用手寫簽名');
      }
    },
    onError: (e: Error) => {
      console.error('[ManagerDecideForm] import signature failed', e);
      message.error(e.message || '導入簽名檔失敗');
    },
  });

  /** SignaturePadModal 完成回呼：把手寫 PNG 上傳至 OSS 並記錄 fileId。 */
  const handleSignatureSubmit = async (file: File) => {
    try {
      setSigUploading(true);
      const id = await uploadSignature(file);
      setSignatureFileId(id);
      message.success('簽名已保存');
      setSigOpen(false);
    } catch (e) {
      console.error('manager signature upload failed', e);
      const msg = e instanceof Error ? e.message : '簽名上傳失敗';
      message.error(msg);
      throw e;
    } finally {
      setSigUploading(false);
    }
  };

  const onFinish = async (values: {
    decision: 'accept' | 'reject_fixable' | 'reject_terminal';
    managerOpinion?: string;
    rejectionReason?: string;
  }) => {
    // 序60.3：簽名非必填——可先批示、簽名檔事後補
    setSubmitting(true);
    try {
      await onSubmit({
        decision: values.decision,
        managerOpinion: values.managerOpinion,
        managerSignatureFileId: signatureFileId ?? undefined,
        gmSignDate: signDate.format('YYYY-MM-DD'),
        rejectionReason: values.rejectionReason,
        version,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      {/*
        序60.3：總經理批示決議 —— 顯性只保留【受理，發TAF證書】/【不受理】兩選項。
        映射既有 conclusion 枚舉：accept=受理；不受理選 reject_terminal（結案）。
        reject_fixable（不受理但可補正重評）為既有流程枚舉，本表單不作顯性選項
        （保留 DB 枚舉值不刪；如需可補正重評由管理組於後續輪次重新發起評審）。
      */}
      <Form.Item name="decision" label="決議" rules={[{ required: true, message: '請選擇決議' }]}>
        <Radio.Group optionType="button">
          <Radio.Button value="accept">受理，發 TAF 認證標誌證書</Radio.Button>
          <Radio.Button value="reject_terminal">不受理</Radio.Button>
        </Radio.Group>
      </Form.Item>

      {needReason && (
        <Form.Item
          name="rejectionReason"
          label="拒絕原因"
          rules={[{ required: true, message: '不受理時必須填寫拒絕原因' }]}
        >
          <Input.TextArea rows={3} maxLength={500} showCount />
        </Form.Item>
      )}

      {/* 序160：批示意見欄改標籤「請填寫不受理原因」（不受理時填寫，受理免填） */}
      <Form.Item name="managerOpinion" label="請填寫不受理原因">
        <Input.TextArea rows={3} maxLength={500} showCount />
      </Form.Item>

      {/* 序60.1：簽名優先導入簽名檔（預設總經理本人簽名檔）；非必填 */}
      <Form.Item
        label="總經理簽名（非必填）"
        tooltip="優先導入員工檔案上傳的簽名檔；未設定簽名檔時可手寫簽名"
      >
        <Space>
          <Button
            type="primary"
            ghost
            loading={importSignatureMut.isPending}
            onClick={() => importSignatureMut.mutate()}
          >
            導入簽名檔
          </Button>
          <Button onClick={() => setSigOpen(true)}>
            {signatureFileId ? '重新手寫簽名' : '手寫簽名'}
          </Button>
          {signatureFileId && <Tag color="success">已簽名</Tag>}
        </Space>
      </Form.Item>

      {/* 序60.1：簽名日期預設當日、可修改 */}
      <Form.Item label="簽名日期">
        <DatePicker
          value={signDate}
          allowClear={false}
          onChange={(d) => d && setSignDate(d)}
          format="YYYY-MM-DD"
        />
      </Form.Item>

      <Space>
        <Button type="primary" htmlType="submit" loading={submitting}>
          提交批示
        </Button>
      </Space>

      {/* 總經理手寫簽名彈窗 */}
      <SignaturePadModal
        open={sigOpen}
        title="總經理簽名"
        onCancel={() => setSigOpen(false)}
        loading={sigUploading}
        onSubmit={handleSignatureSubmit}
      />
    </Form>
  );
}
